/* eslint-disable no-bitwise */
import { Agreement, Plan, useCurrentAgreement } from '@economic/react-app';
import { DimensionsAccess } from '../types';

/** InternalAdvanced and InternalAdvancedMicro have access to Multiple Dimensions */
const internalPlanIdsWithMultipleDimensions = [156, 157];

/** Returns the current user's access level to the Dimensions module. */
function useDimensionsAccessLevel(): DimensionsAccess;
/**
 * Returns true if the user has any of the provided access levels.
 * Use multiple parameters to check for any, or combine them with | to check for all.
 * @example
 * useDimensionsAccessLevel(DimensionsAccess.HasDimension) // true if the user has the Dimension module.
 * useDimensionsAccessLevel(DimensionsAccess.HasDimension, DimensionsAccess.HasAdvancedPackage) // true if the user has the Dimension module *or* the advanced package.
 * useDimensionsAccessLevel(DimensionsAccess.HasDimension | DimensionsAccess.HasAdvancedPackage) // true if the user has the Dimension module *and* the advanced package.
 * */
function useDimensionsAccessLevel(...accessLevels: DimensionsAccess[]): boolean;
function useDimensionsAccessLevel(...accessLevels: DimensionsAccess[]): DimensionsAccess | boolean {
    const currentAgreement = useCurrentAgreement();
    return checkDimensionsAccessLevel(currentAgreement, ...accessLevels);
}

function checkDimensionsAccessLevel(agreement: Agreement): DimensionsAccess;
function checkDimensionsAccessLevel(agreement: Agreement, ...accessLevels: DimensionsAccess[]): boolean;
function checkDimensionsAccessLevel(agreement: Agreement, ...accessLevels: DimensionsAccess[]): DimensionsAccess | boolean {
    const currentLevel = getLevel(agreement);
    if (accessLevels.length === 0) return currentLevel;

    return accessLevels.some(accessLevel => (currentLevel & accessLevel) === accessLevel);
}

export default useDimensionsAccessLevel;
export { checkDimensionsAccessLevel };

function getLevel(currentAgreement: Agreement): DimensionsAccess {
    let level = 0;

    if (currentAgreement.modules.dimensions) level |= DimensionsAccess.HasDimension;
    if (
        currentAgreement.subscription.level === Plan.Advanced ||
        (currentAgreement.subscription.level === Plan.Internal &&
            internalPlanIdsWithMultipleDimensions.includes(currentAgreement.subscription.id))
    )
        level |= DimensionsAccess.HasAdvancedPackage;

    if (level === 0) level = DimensionsAccess.NoAccess;

    return level as DimensionsAccess;
}
